import { isEmpty, isNil } from 'lodash';
import { useMemo } from 'react';
import { buildCardField } from '@shared/data/cards/buildCardField';
import { Field } from '@shared/data/entity/fields/Field';
import { CURRENT_USER_TOKEN } from '@shared/data/entity/fields/PersonField';
import { ScheduledAutomation } from '@shared/data/entity/fields/ScheduledAutomationField';
import { Subform } from '@shared/data/entity/fields/SubformField';
import { FieldRecord, RawFieldData } from '@shared/scenes/entity/types';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import {
  isAuditTagComponent,
  isDateComponent,
  isEmailComponent,
  isInputComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isScheduledAutomationComponent,
  isSubformComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';
import { useProfile } from '@shared/util/hooks';

interface UseDefaultFieldsProps {
  workflowTemplateId?: string | null;
  viewTemplate?: API.ViewTemplateFragment;
  isViewTemplateRequired?: boolean;
  queryFieldOverrides?: RawFieldData;
}

export function useDefaultFields({
  workflowTemplateId,
  viewTemplate,
  isViewTemplateRequired = true,
  queryFieldOverrides,
}: UseDefaultFieldsProps): FieldRecord | null {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId ?? undefined);
  const { components: allComponents } = workflowTemplate ?? {};
  const components = useMemo(() => allComponents?.filter(isInputComponent) ?? [], [allComponents]);
  const { profile, loading: profileLoading } = useProfile();

  return useMemo(() => {
    if (isEmpty(components) || profileLoading) {
      return null;
    }
    if (isViewTemplateRequired && isNil(viewTemplate)) {
      return null;
    }
    let fieldsBuilder: { [componentId: string]: Field } = {};
    components.forEach(component => {
      const fieldOverrides = viewTemplate?.componentsViewOptions?.find(option => {
        return option.componentId === component.id;
      })?.overrides;
      const builtField = buildCardField({
        entity: null,
        component,
        refs: {
          personRefs: null,
          workflowEntityRefs: null,
          workflowTemplateRefs: null,
          recurringEntitiesScheduleRefs: null,
        },
        defaultData: getFieldDefault({
          component,
          fieldOverrides,
          queryFieldOverrides,
          currentUser: profile,
        }),
        currentUser: profile,
      });
      if (!isNil(builtField)) {
        fieldsBuilder[component.id] = builtField;
      }
    });

    return fieldsBuilder;
  }, [
    components,
    viewTemplate,
    queryFieldOverrides,
    profile,
    profileLoading,
    isViewTemplateRequired,
  ]);
}

export function getFieldDefault({
  component,
  fieldOverrides,
  queryFieldOverrides,
  currentUser,
}: {
  component?: API.AnyComponent | null;
  fieldOverrides?: API.AnyComponentOverrides | null;
  queryFieldOverrides?: RawFieldData;
  currentUser?: User;
}) {
  // Key presence, not value, decides whether a query override applies. An explicit null
  // means "leave this field empty" (e.g. creating from a collection view's empty group),
  // so it suppresses the component default instead of falling through to it.
  if (!isNil(component) && !isNil(queryFieldOverrides) && component.id in queryFieldOverrides) {
    return queryFieldOverrides[component.id] ?? null;
  }

  const componentToUse =
    !isNil(fieldOverrides) && 'default' in fieldOverrides && !isNil(fieldOverrides.default)
      ? fieldOverrides
      : component;

  if (isNil(componentToUse)) {
    return null;
  }

  if (isTextComponent(componentToUse)) {
    if (isNil(componentToUse.default?.text) || isEmpty(componentToUse.default?.text)) {
      return null;
    }
    return componentToUse.default.text;
  }

  if (isTagComponent(componentToUse) || isAuditTagComponent(componentToUse)) {
    if (isNil(componentToUse.default?.value) || isEmpty(componentToUse.default?.value)) {
      return null;
    }
    return componentToUse.default.value;
  }

  if (isScheduledAutomationComponent(componentToUse)) {
    if (
      isNil(componentToUse.default) ||
      isNil(componentToUse.default?.automationTriggerDurations) ||
      isEmpty(componentToUse.default?.automationTriggerDurations)
    ) {
      return null;
    }
    const scheduledAutomation: ScheduledAutomation = {
      automationTriggerDurations: componentToUse.default.automationTriggerDurations.filter(
        (v, i) =>
          !isNil(componentToUse.default?.automationTriggerDurations)
            ? componentToUse.default?.automationTriggerDurations.indexOf(v) === i
            : false,
      ),
    };
    return scheduledAutomation;
  }

  if (isRelatedCardComponent(componentToUse)) {
    if (isNil(componentToUse.default?.cardIds) || isEmpty(componentToUse.default?.cardIds)) {
      return null;
    }
    return componentToUse.default.cardIds.map(id => ({ id }));
  }

  if (isPersonComponent(componentToUse)) {
    if (!isEmpty(componentToUse.default?.userIds)) {
      return componentToUse.default?.userIds;
    }
    if (componentToUse.default?.token === CURRENT_USER_TOKEN && !isNil(currentUser)) {
      return [currentUser.id];
    }
    return null;
  }

  if (isEmailComponent(componentToUse)) {
    if (isNil(componentToUse.default?.value) || isEmpty(componentToUse.default?.value)) {
      return null;
    }
    return componentToUse.default.value;
  }

  if (isDateComponent(componentToUse)) {
    const offsetMs = componentToUse.default?.offsetMs;
    if (isNil(offsetMs)) {
      return null;
    }
    return new Date(Date.now() + offsetMs).toISOString();
  }

  if (isSubformComponent(componentToUse)) {
    if (
      isNil(componentToUse.default?.workflowTemplateId) ||
      isEmpty(componentToUse.default?.workflowTemplateId)
    ) {
      return null;
    }
    const subform: Subform = {
      workflowTemplateId: componentToUse.default.workflowTemplateId,
      data: {},
    };
    return subform;
  }

  return null;
}
